import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, Grid, IconButton,
  InputAdornment, InputLabel, MenuItem, Paper, Select, Snackbar,
  Alert, TextField, Tooltip, Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SyncIcon from '@mui/icons-material/Sync';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { jwtDecode } from 'jwt-decode';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

// ─── Status helpers ────────────────────────────────────────────────────────

const MERCHANT_STATUS_META = {
  NEW:                { color: 'default',  label: 'New',              canUpdate: true,  canEdit: false },
  DRAFT:              { color: 'warning',  label: 'Draft',            canUpdate: true,  canEdit: true  },
  INPROGRESS:         { color: 'info',     label: 'In Progress',      canUpdate: true,  canEdit: false },
  PENDING:            { color: 'info',     label: 'Pending',          canUpdate: true,  canEdit: false },
  AUTOAPPROVED:       { color: 'success',  label: 'Auto Approved',    canUpdate: false, canEdit: false },
  APPROVED:           { color: 'success',  label: 'Approved',         canUpdate: false, canEdit: false },
  AUTOAPPROVEDPARTIAL:{ color: 'success',  label: 'Partial Approved', canUpdate: true,  canEdit: false },
  REFERBACK:          { color: 'warning',  label: 'Refer Back',       canUpdate: false, canEdit: true  },
  FAILED:             { color: 'error',    label: 'Failed',           canUpdate: false, canEdit: true  },
  CLOSED:             { color: 'error',    label: 'Closed',           canUpdate: false, canEdit: false },
};

const TERMINAL_STATUS_META = {
  PENDING:     { color: 'warning' },
  APPROVED:    { color: 'success' },
  VERIFIED:    { color: 'success' },
  ACTIVATED:   { color: 'success' },
  REFERBACK:   { color: 'warning' },
  FAILED:      { color: 'error'   },
  DEACTIVATED: { color: 'error'   },
};

const statusMeta = (s) => MERCHANT_STATUS_META[(s && typeof s === 'string') ? s.toUpperCase() : ''] || { color: 'default', label: s || '—', canUpdate: true, canEdit: false };
const termMeta   = (s) => TERMINAL_STATUS_META[(s && typeof s === 'string') ? s.toUpperCase() : ''] || { color: 'default' };

// ─── Component ─────────────────────────────────────────────────────────────

const PendingOnboarding = () => {
  const { can } = usePermissions();
  const canCheckStatus = can('merchants.pending.checkStatus');
  const canEditResubmit = can('merchants.pending.editResubmit');
  const [rows, setRows]               = useState([]);
  const [loading, setLoading]         = useState(false);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(0);
  const [pageSize, setPageSize]       = useState(20);
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [userId, setUserId]           = useState(null);
  const [token, setToken]             = useState(null);

  const [detailShop, setDetailShop]   = useState(null);
  const [detailOpen, setDetailOpen]   = useState(false);

  const [editShop, setEditShop]       = useState(null);
  const [editForm, setEditForm]       = useState({});
  const [editOpen, setEditOpen]       = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [updateShop, setUpdateShop]   = useState(null);
  const [updateOpen, setUpdateOpen]   = useState(false);
  const [updating, setUpdating]       = useState(false);

  const [snack, setSnack]             = useState({ open: false, msg: '', severity: 'success' });

  // ── Auth ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const t = sessionStorage.getItem('token');
    if (t) {
      setToken(t);
      setUserId(jwtDecode(t).userId);
    }
  }, []);

  // ── Fetch list ──────────────────────────────────────────────────────────
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/acemoney-registration/pending-list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: page + 1, limit: pageSize, search, status: statusFilter },
      });
      setRows((data.data || []).map((s, i) => ({ ...s, id: s._id || i })));
      setTotal(data.total || 0);
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.message) ? e.response.data.message : 'Failed to load list';
      showSnack(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, search, statusFilter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const showSnack = (msg, severity = 'success') =>
    setSnack({ open: true, msg, severity });

  // ── Check / Update status ───────────────────────────────────────────────
  const handleUpdate = async () => {
    if (!updateShop || !canCheckStatus) return;
    setUpdating(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/acemoney-registration/update-status/${updateShop._id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUpdateOpen(false);
      if (data.registered) {
        showSnack(`✅ ${updateShop.sellerName} approved by AceMoney!`);
      } else if (data.failed) {
        showSnack(`❌ ${data.message}`, 'error');
      } else {
        showSnack(`ℹ️ ${data.message}`, 'info');
      }
      fetchList();
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.message) ? e.response.data.message : 'Update failed';
      showSnack(msg, 'error');
    } finally {
      setUpdating(false);
    }
  };

  // ── Edit & Resubmit ─────────────────────────────────────────────────────
  const openEdit = (shop) => {
    if (!canEditResubmit) return;
    setEditShop(shop);
    setEditForm({
      sellerName:             shop.sellerName || '',
      mobileNumber:           shop.mobileNumber || '',
      emailId:                shop.emailId || '',
      businessName:           shop.businessName || '',
      pan:                    shop.pan || '',
      promoterAadhaar:        shop.promoterAadhaar || '',
      dob:                    shop.dob || '',
      gender:                 shop.gender || 'Male',
      addressLine1:           shop.addressLine1 || '',
      addressLine2:           shop.addressLine2 || '',
      pincode:                shop.pincode || '',
      city:                   shop.city || '',
      district:               shop.district || '',
      settlementAccountNumber:shop.settlementAccountNumber || '',
      settlementAccountIfsc:  shop.settlementAccountIfsc || '',
      gstNumber:              shop.gstNumber || '',
      vpaCode:                shop.vpaCode || '',
    });
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editShop || !userId || !canEditResubmit) return;
    setEditSubmitting(true);
    try {
      const { data } = await axios.put(
        `${API_BASE_URL}/acemoney-registration/retry/${userId}/${editShop._id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditOpen(false);
      if (data.success) {
        showSnack(data.message || 'Resubmitted successfully');
      } else {
        showSnack(data.message || 'Resubmit failed', 'error');
      }
      fetchList();
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.message) ? e.response.data.message : 'Resubmit failed';
      showSnack(msg, 'error');
    } finally {
      setEditSubmitting(false);
    }
  };

  // ── Columns ─────────────────────────────────────────────────────────────
  const columns = [
    {
      field: 'merchantId',
      headerName: 'Merchant ID',
      width: 150,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.dark' }}>
          {row.acemoneyUsercode || row.sellerIdentifier || row.vpaCode || row.merchantId || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'sellerName',
      headerName: 'Merchant Name',
      width: 170,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
          {value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'businessName',
      headerName: 'Shop Name',
      width: 180,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#0f172a' }}>
          {value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'shopType',
      headerName: 'Shop Type',
      width: 140,
      renderCell: ({ value, row }) => (
        <Typography variant="body2" sx={{ color: '#475569' }}>
          {value || row.bussiness || 'General'}
        </Typography>
      ),
    },
    {
      field: 'stateCode',
      headerName: 'STATE',
      width: 140,
      renderCell: ({ value, row }) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value || row.state || 'Kerala'}
        </Typography>
      ),
    },
    {
      field: 'district',
      headerName: 'DISTRICT',
      width: 140,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'village',
      headerName: 'CONSTITUENCY',
      width: 140,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ color: '#475569' }}>
          {row.city || row.post || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'pincode',
      headerName: 'Pincode',
      width: 120,
      renderCell: ({ value }) => (
        <Chip label={value || 'N/A'} size="small" variant="outlined" />
      ),
    },
    {
      field: 'assignedQPayCenter',
      headerName: 'Q-Pay Center ID',
      width: 180,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
          {value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'assignedStateHead',
      headerName: 'State Head ID',
      width: 170,
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
          {value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      renderCell: ({ row }) => {
        const m = statusMeta(row.acemoneyMerchantStatus);
        return (
          <Box display="flex" gap={0.5}>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={() => { setDetailShop(row); setDetailOpen(true); }}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {m.canUpdate && canCheckStatus && (
              <Tooltip title="Check / Update Status">
                <IconButton size="small" color="info" onClick={() => { setUpdateShop(row); setUpdateOpen(true); }}>
                  <SyncIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {m.canEdit && canEditResubmit && (
              <Tooltip title="Edit & Resubmit">
                <IconButton size="small" color="warning" onClick={() => openEdit(row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      <BackButton />

      <Typography variant="h5" fontWeight="bold" mb={1}>
        Pending Onboarding
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Shops submitted to AceMoney that are waiting for approval, referred back, or failed.
        Use <b>Check Status</b> after 24 hrs to pull the latest decision from AceMoney.
        Use <b>Edit &amp; Resubmit</b> for shops that were failed or referred back with corrections needed.
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5} md={4}>
            <TextField
              fullWidth size="small" placeholder="Search name, PAN, mobile, usercode…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setSearch(''); setPage(0); }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Merchant Status</InputLabel>
              <Select
                value={statusFilter}
                label="Merchant Status"
                onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(MERCHANT_STATUS_META).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs="auto">
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={fetchList}>
              Refresh
            </Button>
          </Grid>
          <Grid item xs="auto">
            <Chip label={`${total} total`} color="primary" variant="outlined" />
          </Grid>
        </Grid>
      </Paper>

      {/* Quick status filter (same state as the Merchant Status dropdown) */}
      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
        <Chip
          label="All"
          color="primary"
          size="small"
          variant={statusFilter === '' ? 'filled' : 'outlined'}
          onClick={() => { setStatusFilter(''); setPage(0); }}
        />
        {Object.entries(MERCHANT_STATUS_META).map(([k, v]) => (
          <Chip
            key={k}
            label={v.label}
            color={v.color}
            size="small"
            variant={statusFilter === k ? 'filled' : 'outlined'}
            onClick={() => { setStatusFilter(statusFilter === k ? '' : k); setPage(0); }}
          />
        ))}
      </Box>

      {/* Table */}
      <Paper sx={{ height: 520 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          rowCount={total}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page: p, pageSize: ps }) => { setPage(p); setPageSize(ps); }}
          pageSizeOptions={[10, 20, 50]}
          disableRowSelectionOnClick
          sx={{ border: 0 }}
        />
      </Paper>

      {/* ── Detail Dialog ────────────────────────────────────────────────── */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Shop Details</DialogTitle>
        <DialogContent dividers>
          {detailShop && (
            <Grid container spacing={1.5}>
              {[
                ['Seller Name',    detailShop.sellerName],
                ['Business Name',  detailShop.businessName],
                ['Mobile',         detailShop.mobileNumber],
                ['Email',          detailShop.emailId],
                ['PAN',            detailShop.pan],
                ['Usercode',       detailShop.acemoneyUsercode || '—'],
                ['VPA Code',       detailShop.vpaCode || '—'],
              ].map(([label, value]) => (
                <Grid item xs={12} sm={6} key={label}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" fontWeight="medium">{value}</Typography>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                  <Chip label={`Merchant: ${detailShop.acemoneyMerchantStatus || '—'}`} color={statusMeta(detailShop.acemoneyMerchantStatus).color} />
                  {detailShop.acemoneyTerminalStatus && (
                    <Chip label={`Terminal: ${detailShop.acemoneyTerminalStatus}`} color={termMeta(detailShop.acemoneyTerminalStatus).color} variant="outlined" />
                  )}
                  <Chip label={`Internal: ${detailShop.acemoneyRegistrationStatus}`} color={detailShop.acemoneyRegistrationStatus === 'failed' ? 'error' : 'warning'} variant="outlined" />
                </Box>
              </Grid>
              {detailShop.acemoneyFailureReason && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <b>Reason:</b> {detailShop.acemoneyFailureReason}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Submitted: {detailShop.createdAt ? new Date(detailShop.createdAt).toLocaleString('en-IN') : '—'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
          {detailShop && canEditResubmit && statusMeta(detailShop.acemoneyMerchantStatus).canEdit && (
            <Button color="warning" variant="contained" onClick={() => { setDetailOpen(false); openEdit(detailShop); }}>
              Edit & Resubmit
            </Button>
          )}
          {detailShop && canCheckStatus && statusMeta(detailShop.acemoneyMerchantStatus).canUpdate && (
            <Button color="info" variant="contained" onClick={() => { setDetailOpen(false); setUpdateShop(detailShop); setUpdateOpen(true); }}>
              Check Status
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Update Confirm Dialog ─────────────────────────────────────────── */}
      <Dialog open={updateOpen} onClose={() => !updating && setUpdateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Check / Update Status</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will send a <b>service_type = UPDATE</b> request to AceMoney for:
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold" mt={1}>
            {(updateShop && updateShop.sellerName) ? updateShop.sellerName : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Current status: {(updateShop && updateShop.acemoneyMerchantStatus) ? updateShop.acemoneyMerchantStatus : '—'}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            Use this after 24 hrs from initial submission. AceMoney will return the latest decision.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateOpen(false)} disabled={updating}>Cancel</Button>
          <Button
            variant="contained" color="info"
            onClick={handleUpdate}
            disabled={updating}
            startIcon={updating ? <CircularProgress size={16} /> : <SyncIcon />}
          >
            {updating ? 'Updating…' : 'Update Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit & Resubmit Dialog ────────────────────────────────────────── */}
      <Dialog open={editOpen} onClose={() => !editSubmitting && setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit & Resubmit — {(editShop && editShop.sellerName) ? editShop.sellerName : ''}
          <Typography variant="caption" display="block" color="text.secondary">
            Merchant Status: {(editShop && editShop.acemoneyMerchantStatus) ? editShop.acemoneyMerchantStatus : '—'} &nbsp;|&nbsp;
            {(editShop && editShop.acemoneyFailureReason) ? `Reason: ${editShop.acemoneyFailureReason}` : ''}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {(editShop && editShop.acemoneyFailureReason) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <b>AceMoney Reason:</b> {editShop.acemoneyFailureReason}
            </Alert>
          )}
          <Grid container spacing={2}>
            {[
              { key: 'sellerName',             label: 'Seller Name',              required: true  },
              { key: 'mobileNumber',            label: 'Mobile Number',            required: true  },
              { key: 'emailId',                 label: 'Email',                    required: true  },
              { key: 'businessName',            label: 'Business Name',            required: true  },
              { key: 'pan',                     label: 'PAN Number',               required: true  },
              { key: 'promoterAadhaar',         label: 'Aadhaar Number',           required: true  },
              { key: 'dob',                     label: 'Date of Birth (YYYY-MM-DD)',required: true  },
              { key: 'addressLine1',            label: 'Address Line 1',           required: true  },
              { key: 'addressLine2',            label: 'Address Line 2',           required: true  },
              { key: 'pincode',                 label: 'Pincode',                  required: true  },
              { key: 'city',                    label: 'City',                     required: false },
              { key: 'district',                label: 'District',                 required: false },
              { key: 'settlementAccountNumber', label: 'Account Number',           required: true  },
              { key: 'settlementAccountIfsc',   label: 'IFSC Code',                required: true  },
              { key: 'vpaCode',                 label: 'UPI / VPA Code',           required: true  },
              { key: 'gstNumber',               label: 'GST Number (optional)',    required: false },
            ].map(({ key, label, required }) => (
              <Grid item xs={12} sm={6} key={key}>
                <TextField
                  fullWidth size="small"
                  label={label}
                  required={required}
                  value={editForm[key] || ''}
                  onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Gender</InputLabel>
                <Select
                  value={editForm.gender || 'Male'}
                  label="Gender"
                  onChange={e => setEditForm(f => ({ ...f, gender: e.target.value }))}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editSubmitting}>Cancel</Button>
          <Button
            variant="contained" color="warning"
            onClick={handleEditSubmit}
            disabled={editSubmitting}
            startIcon={editSubmitting ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {editSubmitting ? 'Submitting…' : 'Resubmit to AceMoney'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PendingOnboarding;
